import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useCallback, useState } from 'react';
import { useGetDtoolsAuthDetails } from 'src/graphql/entities/integrations/dtools/hooks/use-get-dtools-auth-details';
import { useGetPortalioAuthDetails } from 'src/graphql/entities/integrations/portalio/hooks/use-get-portalio-auth-details';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { DtoolsProjectsList } from './dtools-projects-list';
import { ImportProposalsSearch } from './import-proposals-search';
import { PortalioProposalsList } from './portalio-proposals-list';
import { SourceButton } from './source-button';

export const ImportProposalsModal = () => {
  const theme = useTheme();
  const { closeModal } = useModalMethods(Modals.ImportProposals);
  const { modalProps } = useModalState(Modals.ImportProposals);
  const [searchValue, setSearchValue] = useState('');
  const [selectedProposals, setSelectedProposals] = useState<
    { id: number | string; name: string }[]
  >([]);
  const [currentView, setCurrentView] = useState<'SELECT_SOURCE_VIEW' | 'SELECT_ITEMS_VIEW'>(
    'SELECT_SOURCE_VIEW',
  );
  const [selectedSource, setSelectedSource] = useState<'portalio' | 'dtools' | null>(null);
  const onImport = useCallback(() => {
    if (selectedSource === null) {
      return;
    }
    modalProps?.onItemsSelected(selectedProposals, selectedSource);
    closeModal();
  }, [closeModal, modalProps, selectedProposals, selectedSource]);

  const { portalioAuthDetails } = useGetPortalioAuthDetails();
  const portalioAuthed = !!portalioAuthDetails?.id;

  const { dtoolsAuthDetails } = useGetDtoolsAuthDetails();
  const dtoolsAuthed = !!dtoolsAuthDetails?.id;

  if (currentView === 'SELECT_SOURCE_VIEW') {
    return (
      <Dialog open onClose={closeModal} fullWidth maxWidth="sm">
        <DialogTitle>
          <Stack>
            <Typography>Import from...</Typography>
          </Stack>
        </DialogTitle>
        <Divider />
        <DialogContent
          dividers
          sx={{
            paddingX: theme.spacing(3),
            paddingY: theme.spacing(),
          }}
        >
          <SourceButton
            title="portal.io"
            subtitle="Import proposals from portal.io"
            disabled={!portalioAuthed}
            onClick={() => {
              setSelectedSource('portalio');
              setCurrentView('SELECT_ITEMS_VIEW');
            }}
          />

          <SourceButton
            title="D-Tools Cloud"
            subtitle="Import proposals from D-Tools Cloud"
            disabled={!dtoolsAuthed}
            onClick={() => {
              setSelectedSource('dtools');
              setCurrentView('SELECT_ITEMS_VIEW');
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal} size="small">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
  return (
    <Dialog open onClose={closeModal} fullWidth maxWidth="sm">
      <DialogTitle>
        <Stack>
          {selectedSource === 'portalio' ? (
            <>
              <Typography>Import from portal.io</Typography>
              <Typography variant="subtitle2">
                Select proposals from portal.io to import as files
              </Typography>
            </>
          ) : (
            <>
              <Typography>Import from D-Tools Cloud</Typography>
              <Typography variant="subtitle2">
                Select projects from D-Tools Cloud to import as files
              </Typography>
            </>
          )}
        </Stack>
      </DialogTitle>
      <DialogActions
        sx={{
          paddingX: theme.spacing(3),
        }}
      >
        <ImportProposalsSearch onChange={setSearchValue} />
      </DialogActions>
      <DialogContent
        dividers
        sx={{
          paddingX: theme.spacing(3),
          paddingY: theme.spacing(),
        }}
      >
        {selectedSource === 'portalio' ? (
          <PortalioProposalsList
            onNavigateAway={closeModal}
            onProjectsSelected={setSelectedProposals}
            filterText={searchValue}
          />
        ) : null}
        {selectedSource === 'dtools' ? (
          <DtoolsProjectsList
            filterText={searchValue}
            onProjectsSelected={setSelectedProposals}
            initialSelectedIds={selectedProposals.map((p) => p.id.toString())}
          />
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeModal} size="small">
          Close
        </Button>
        <Button
          variant="contained"
          onClick={onImport}
          size="small"
          startIcon={<CloudDownloadIcon />}
          disabled={selectedProposals.length === 0}
        >
          {selectedProposals.length ? `Import (${selectedProposals.length})` : 'Import'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
